/**
 * Formats the address to only return the city, state, and zip code from the detailed API response.
 *
 * @param {string} address - The full address string to format.
 * @returns {string} The formatted address containing only the city, state, and zip code.
 */
export function formatSelectedLocation(address) {
  const splitAddress = address.split(",");
  let formattedAddress = splitAddress;

  if (splitAddress.length > 3) {
    // If the address has more than 3 parts, grab the middle parts (city, state, zip)
    formattedAddress = splitAddress.slice(1, splitAddress.length - 1);
  } else if (splitAddress.length === 3) {
    // If the address has exactly 3 parts, grab the first two parts (city, state)
    formattedAddress = splitAddress.slice(0, splitAddress.length - 1);
  }

  return formattedAddress.join(", ");
}

/**
 * Modifies the back-end response to meet the expected client-side format.
 *
 * @param {Object} param0 - The location object from the back-end response.
 * @param {Object} param0.location - The location details containing latitude and longitude.
 * @param {string} param0.place_name - The name of the place.
 * @returns {Object} The formatted location object containing name, latitude, and longitude.
 */
export const formatSuggestedLocation = ({ location, place_name }) => ({
  name: place_name,
  lat: location.lat,
  lon: location.lon,
});

/**
 * Checks if the given latitude and longitude are within the contiguous United States.
 *
 * @param {number} lat - The latitude to check.
 * @param {number} lon - The longitude to check.
 * @returns {boolean} True if the latitude and longitude are within the contiguous United States, false otherwise.
 */
export const isWithinUS = (lat, lon) => {
  // Rough boundaries for the contiguous US
  const minLatContiguous = 24.396308; // Southernmost point (Florida)
  const maxLatContiguous = 49.384358; // Northernmost point (excludes Alaska)
  const minLonContiguous = -125.0; // Westernmost point (California)
  const maxLonContiguous = -66.93457; // Easternmost point (Maine)

  // Boundaries for Hawaii
  const minLatHawaii = 18.910361; // Southernmost point
  const maxLatHawaii = 28.402123; // Northernmost point
  const minLonHawaii = -178.334698; // Westernmost point
  const maxLonHawaii = -154.806773; // Easternmost point

  // Boundaries for Alaska
  const minLatAlaska = 51.209712; // Southernmost point
  const maxLatAlaska = 71.5388; // Northernmost point
  const minLonAlaska = -179.148909; // Westernmost point
  const maxLonAlaska = -129.979479; // Easternmost point

  // Exclude Canada (example exclusion for Ontario region)
  const isCanada =
    lat >= 41 &&
    lat <= 49.384358 && // Between the US-Canada border
    lon >= -85.0 &&
    lon <= -74.0; // Longitudes around Ontario/Quebec

  // Check if within contiguous US
  const inContiguousUS =
    lat >= minLatContiguous &&
    lat <= maxLatContiguous &&
    lon >= minLonContiguous &&
    lon <= maxLonContiguous &&
    !isCanada; // Ensure it's not in excluded Canada region

  // Check if within Hawaii
  const inHawaii =
    lat >= minLatHawaii && lat <= maxLatHawaii && lon >= minLonHawaii && lon <= maxLonHawaii;

  // Check if within Alaska
  const inAlaska =
    lat >= minLatAlaska && lat <= maxLatAlaska && lon >= minLonAlaska && lon <= maxLonAlaska;

  // Return true if in contiguous US, Hawaii, or Alaska
  return inContiguousUS || inHawaii || inAlaska;
};

/**
 * Extracts the zip code from a given location string.
 *
 * This function searches for a 5-digit zip code within the location string.
 *
 * @param {string} location - The location string to search for a zip code.
 * @returns {string|null} The extracted zip code if found, otherwise null.
 */
export const getZipCodeFromLocation = (location) => {
  const zipCodeMatch = location.match(/\b(\d{5}(-\d{4})?|[A-Z]\d[A-Z] \d[A-Z]\d|[A-Z]{2}\d{5})\b/);
  return zipCodeMatch ? zipCodeMatch[0] : null;
};

/**
 * Parses a location string into an object containing city, state, postal code, and country.
 * This function expects the location string to be in a specific format.
 *
 * The location format is expected to be one of the following:
 * - "City, State PostalCode, Country"
 * - "City, State, PostalCode, Country"
 *
 * @param {string} locationString - The location string to parse. This is the location from the database and cannot be changed.
 * @returns {Object|null} An object containing the parsed location details: city, state, postalCode, and country. Returns null if the format does not match.
 */
export const parseLocationFormat = (locationString) => {
  // Define regex patterns as constants
  const REGEX_PATTERNS = [
    /^(.*?),\s*([\w\s]+)\s+(\d{5}(?:-\d{4})?),\s*(.*?)$/, // Format: "City, State PostalCode, Country" (US)
    /^(.*?),\s*([\w\s]+),\s*(\d{5}(?:-\d{4})?),\s*(.*?)$/, // Format: "City, State, PostalCode, Country" (US)
    /^([A-Z]\d[A-Z] \d[A-Z]\d),\s*(.*?),\s*(.*?),\s*(.*?)$/, // Format: "PostalCode, City, State, Country" (Canada)
  ];

  // Iterate over regex patterns to find a match
  for (const regex of REGEX_PATTERNS) {
    const match = locationString.match(regex);
    if (match) {
      // Destructure the matched parts from the regex
      const [, part1, part2, part3, part4] = match;

      // Check if the regex pattern is for the Canadian format
      return regex === REGEX_PATTERNS[2]
        ? { postalCode: part1, city: part2, state: part3, country: part4 } // Return the parsed object for Canadian format
        : { city: part1, state: part2, postalCode: part3, country: part4 }; // Return the parsed object for US format
    }
  }

  // Return null if no match
  return null;
};
